import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ExternalLink, FileText, PlayCircle, Link2, Loader2, X } from "lucide-react";
import { api, toEmbedUrl } from "../lib/api";
import { useI18n } from "../lib/i18n";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";

const fixUrl = (u) => { const v = String(u || "").trim(); if (!v) return ""; return /^https?:\/\//i.test(v) ? v : "https://" + v; };

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeVideo, setActiveVideo] = useState(null);

  useEffect(() => {
    api
      .get(`/projects/${id}`)
      .then((r) => setProject(r.data))
      .catch((e) => {
        if (e.response?.status === 401) navigate("/login");
        else setError("Projekat nije pronađen.");
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading)
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-5 py-32 flex justify-center">
          <Loader2 className="w-8 h-8 text-nhgreen animate-spin" />
        </div>
      </Layout>
    );

  if (error || !project)
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-5 py-32 text-center font-mono text-white/60">{error}</div>
      </Layout>
    );

  const isPending = project.status !== "active";
  const banner = project.video_banner;
  const sections = project.sections || [];

  return (
    <Layout>
      {activeVideo && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 px-4"
          onClick={() => setActiveVideo(null)}
        >
          <div className="relative w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setActiveVideo(null)}
              className="mb-3 flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-white/60 hover:text-nhgreen transition-colors"
            >
              <X className="w-4 h-4" /> ZATVORI
            </button>
            <div className="aspect-video rounded-xl overflow-hidden border border-nhgreen/30 shadow-[0_0_40px_rgba(34,255,119,0.15)]">
              <iframe
                src={toEmbedUrl(activeVideo)}
                title="video"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-12">
        {/* Back + logo */}
        <div className="flex items-center justify-between gap-4">
          <button
            onClick={() => navigate("/")}
            data-testid="detail-back"
            className="flex items-center gap-2 font-mono text-sm text-white/50 hover:text-nhgreen transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> {t("back")}
          </button>
          {project.logo && (
            <img src={project.logo} alt={project.title} className="h-14 w-auto object-contain" />
          )}
        </div>

        {/* Header */}
        <header className="mt-10">
          {project.tag && (
            <span className="inline-block font-mono text-xs tracking-[0.25em] uppercase text-nhgreen border border-nhgreen/40 rounded-full px-4 py-1.5">
              {project.tag}
            </span>
          )}
          <div className="flex items-center gap-4 flex-wrap mt-5">
            <h1 className="font-mono font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              {project.title}
            </h1>
            <StatusBadge status={project.status} />
          </div>
          <p className="text-white/70 text-base sm:text-lg leading-relaxed max-w-2xl mt-5">
            {project.tagline}
          </p>
        </header>

        {/* Video banner */}
        {banner && banner.url && (
          <button
            onClick={() => setActiveVideo(banner.url)}
            data-testid="detail-video-banner"
            className="group w-full mt-8 flex items-center gap-5 text-left bg-nhgreen/5 border border-nhgreen/40 rounded-2xl p-6 hover:border-nhgreen hover:shadow-[0_0_24px_rgba(34,255,119,0.2)] transition-[border-color,box-shadow] duration-200"
          >
            <span className="shrink-0 w-12 h-12 rounded-full border border-nhgreen/50 flex items-center justify-center text-nhgreen group-hover:bg-nhgreen/10 transition-colors">
              <PlayCircle className="w-6 h-6" />
            </span>
            <span className="flex-1">
              <span className="block font-mono font-bold text-sm tracking-[0.15em] uppercase text-nhgreen">
                {banner.title}
              </span>
              {banner.subtitle && (
                <span className="block text-xs text-white/50 mt-1.5">{banner.subtitle}</span>
              )}
            </span>
          </button>
        )}

        {project.link && (
          <a
            href={fixUrl(project.link)}
            data-testid="detail-external-link"
            className="inline-flex items-center gap-2 font-mono font-bold text-sm bg-nhgreen text-black rounded-md px-5 py-3 mt-6 hover:bg-[#1ee068] hover:shadow-[0_0_15px_rgba(34,255,119,0.4)] transition-[background-color,box-shadow] duration-200"
          >
            {t("external_link")} <ExternalLink className="w-4 h-4" />
          </a>
        )}

        {project.cta_url && (
          <a
            href={fixUrl(project.cta_url)}
            data-testid="detail-cta"
            className="inline-flex items-center gap-2 font-mono font-bold text-sm bg-nhgreen text-black rounded-md px-6 py-3 mt-6 ml-0 sm:ml-3 hover:bg-[#1ee068] hover:shadow-[0_0_20px_rgba(34,255,119,0.5)] transition-[background-color,box-shadow] duration-200"
          >
            {project.cta_label || "Pridruži se projektu"} <ArrowRight className="w-4 h-4" />
          </a>
        )}

        <div className="h-px bg-white/10 my-10" />

        {isPending ? (
          <div
            data-testid="detail-pending"
            className="bg-nhorange/5 border border-nhorange/30 rounded-2xl p-10 text-center"
          >
            <p className="font-mono text-nhorange text-lg text-glow-orange">{t("pending_content")}</p>
          </div>
        ) : (
          <div className="space-y-12">
            {/* Intro description */}
            {project.description && (
              <p className="text-white/80 text-base sm:text-lg leading-relaxed whitespace-pre-line bg-[#0A0A0A] border border-white/10 border-l-4 border-l-nhgreen rounded-2xl p-6 sm:p-8">
                {project.description}
              </p>
            )}

            {/* Custom sections */}
            {sections.map((s, i) => (
              <div
                key={i}
                className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6 sm:p-8"
              >
                {s.title && (
                  <h3 className="font-mono font-bold text-base tracking-[0.1em] text-nhgreen">
                    {s.title}
                  </h3>
                )}
                {s.body && (
                  <p className="text-white/70 leading-relaxed whitespace-pre-line mt-4">{s.body}</p>
                )}
              </div>
            ))}

            {/* Videos */}
            {project.videos && project.videos.length > 0 && (
              <Section title={t("section_videos")} icon={<PlayCircle className="w-4 h-4" />}>
                <div className={project.videos.length > 1 ? "grid grid-cols-1 sm:grid-cols-2 gap-5" : "grid grid-cols-1 gap-5"}>
                  {project.videos.map((v, i) => (
                    <div key={i} className="aspect-video rounded-xl overflow-hidden border border-white/10 bg-black">
                      <iframe
                        src={toEmbedUrl(v)}
                        title={`video-${i}`}
                        className="w-full h-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* PDF */}
            {project.pdf_url && (
              <Section title={t("section_pdf")} icon={<FileText className="w-4 h-4" />}>
                <div className="space-y-4">
                  <a
                    href={project.pdf_url}
                    data-testid="detail-pdf-download"
                    className="inline-flex items-center gap-2 font-mono font-bold text-sm border border-nhgreen/60 text-nhgreen rounded-md px-6 py-3 hover:bg-nhgreen/10 hover:shadow-[0_0_15px_rgba(34,255,119,0.3)] transition-[background-color,box-shadow] duration-200"
                  >
                    <FileText className="w-4 h-4" /> {t("download_pdf")}
                  </a>
                </div>
              </Section>
            )}

            {/* Tutorials */}
            {project.tutorials && project.tutorials.length > 0 && (
              <Section title={t("section_tutorials")}>
                <div className="space-y-3">
                  {project.tutorials.map((tut, i) => (
                    <a
                      key={i}
                      href={tut.url}
                      className="flex items-center gap-3 bg-[#0A0A0A] border border-white/10 rounded-xl px-5 py-4 hover:border-nhgreen/50 transition-colors group"
                    >
                      {tut.type === "pdf" ? (
                        <FileText className="w-5 h-5 text-nhgreen" />
                      ) : tut.type === "link" ? (
                        <Link2 className="w-5 h-5 text-nhgreen" />
                      ) : (
                        <PlayCircle className="w-5 h-5 text-nhgreen" />
                      )}
                      <span className="font-mono text-sm text-white/80 group-hover:text-white">{tut.title}</span>
                    </a>
                  ))}
                </div>
              </Section>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

function Section({ title, icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-5">
        {icon && <span className="text-nhgreen">{icon}</span>}
        <h2 className="font-mono font-bold text-xs tracking-[0.2em] uppercase text-nhgreen">{title}</h2>
      </div>
      {children}
    </div>
  );
}
